import React from 'react';
import type { Document } from '../App';

interface CostData {
  claudeInputTokens?: number;
  claudeOutputTokens?: number;
  claudeCost?: number;
  llamaInputTokens?: number;
  llamaOutputTokens?: number;
  llamaCost?: number;
  novaInputTokens?: number;
  novaOutputTokens?: number;
  novaCost?: number;
  textractCost?: number;
  totalCost?: number;
  costPerPage?: number;
}

interface DocumentWithCost extends Document {
  costData?: CostData;
  estimatedCostData?: {
    avgEstPages: string;
    bestEstPages: string;
    worstEstPages: string;
    avgCost: string;
    bestCost: string;
    worstCost: string;
    model?: string;
    docTitle?: string;
    costStatus?: 'normal' | 'warning' | 'danger';
  };
}

interface DocumentDetailsProps {
  document: DocumentWithCost;
  onBack: () => void;
}

export const DocumentDetails: React.FC<DocumentDetailsProps> = ({ 
  document, 
  onBack 
}) => {
  const sections = document.sections || [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETE':
      case 'COMPLETED':
      case 'PROCESSED':
        return <span className="badge bg-success">Complete</span>;
      case 'PROCESSING':
      case 'IN_PROGRESS':
        return <span className="badge bg-primary">Processing</span>;
      case 'FAILED':
        return <span className="badge bg-danger">Failed</span>;
      case 'PENDING':
        return <span className="badge bg-warning text-dark">Pending</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  // Format currency for display
  const formatCurrency = (value?: number | null) => {
    if (value === undefined || value === null) return 'N/A';
    return `$${value.toFixed(6)}`;
  };

  // Format number with commas
  const formatNumber = (value?: number | null) => {
    if (value === undefined || value === null) return 'N/A';
    return value.toLocaleString();
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Document Details: {document.documentId}</h5>
          <h6 className="mb-0">Original Filename: {document.originalFilename}</h6>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={onBack}
          >
            Back to List
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="row mb-4">
          <div className="col-md-6">
            <h6>Document Information</h6>
            <ul className="list-group">
              <li className="list-group-item d-flex justify-content-between align-items-center">
                Status
                {getStatusBadge(document.status)}
              </li>
              {document.status === 'PROCESSING' && (
                <>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Progress
                    <span>{document.progress ? Math.round(document.progress) : 0}%</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Sections Completed
                    <span>{document.completedSections || 0} / {document.totalSections || 0}</span>
                  </li>
                </>
              )}
                {document.costData?.totalCost !== undefined && (
                  <li 
                    className={`list-group-item d-flex justify-content-between align-items-center ${
                      document.estimatedCostData?.costStatus === 'danger' 
                        ? 'bg-danger text-white' 
                        : document.estimatedCostData?.costStatus === 'warning' 
                          ? 'bg-warning' 
                          : ''
                    }`}
                  >
                    <div>
                      Total Processing Cost
                      {document.estimatedCostData && (
                        <small className="d-block">
                          {document.estimatedCostData.costStatus === 'danger' 
                            ? 'Exceeds worst-case estimate!' 
                            : document.estimatedCostData.costStatus === 'warning' 
                              ? 'Exceeds average estimate' 
                              : 'Within estimates'}
                        </small>
                      )}
                    </div>
                    <span className="fw-bold">{formatCurrency(document.costData.totalCost)}</span>
                  </li>
                )}
              {document.costData?.costPerPage !== undefined && (
                <li className="list-group-item d-flex justify-content-between align-items-center">
                  Cost Per Page
                  <span>{formatCurrency(document.costData.costPerPage)}</span>
                </li>
              )}
              
            </ul>
          </div>
          <div className="col-md-6">
            {document.status === 'PROCESSING' && (
              <div className="progress mb-3" style={{ height: '30px' }}>
                <div 
                  className="progress-bar" 
                  role="progressbar" 
                  style={{ width: `${document.progress || 0}%` }}
                  aria-valuenow={document.progress || 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  {document.progress ? Math.round(document.progress) : 0}%
                </div>
              </div>
            )}
            
            {/* Only show cost breakdown if we have cost data */}
            {document.costData && (
              <div className="card mt-3">
                <div className="card-header">
                  <h6 className="mb-0">Cost Breakdown</h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Input Tokens</th>
                        <th>Output Tokens</th>
                        <th>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Claude</td>
                        <td>{formatNumber(document.costData.claudeInputTokens)}</td>
                        <td>{formatNumber(document.costData.claudeOutputTokens)}</td>
                        <td>{formatCurrency(document.costData.claudeCost)}</td>
                      </tr>
                      <tr>
                        <td>Llama</td>
                        <td>{formatNumber(document.costData.llamaInputTokens)}</td>
                        <td>{formatNumber(document.costData.llamaOutputTokens)}</td>
                        <td>{formatCurrency(document.costData.llamaCost)}</td>
                      </tr>
                      <tr>
                        <td>Nova</td>
                        <td>{formatNumber(document.costData.novaInputTokens)}</td>
                        <td>{formatNumber(document.costData.novaOutputTokens)}</td>
                        <td>{formatCurrency(document.costData.novaCost)}</td>
                      </tr>
                      <tr>
                        <td>Textract</td>
                        <td colSpan={2}>-</td>
                        <td>{formatCurrency(document.costData.textractCost)}</td>
                      </tr>
                      <tr className="table-primary">
                        <td colSpan={3}><strong>Total</strong></td>
                        <td><strong>{formatCurrency(document.costData.totalCost)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="row mb-4">
        <div className="col-md-12">
        {document.estimatedCostData && (
              <div className="card mt-3">
                <div className="card-header">
                  <h6 className="mb-0">Estimated Cost Data</h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Model</th>
                        <th>Title</th>
                        <th>Avg Pages</th>
                        <th>Best Pages</th>
                        <th>Worst Pages</th>
                        <th>Avg Cost</th>
                        <th>Best Cost</th>
                        <th>Worst Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{document.estimatedCostData.model || 'N/A'}</td>
                        <td>{document.estimatedCostData.docTitle || 'N/A'}</td>
                        <td>{document.estimatedCostData.avgEstPages}</td>
                        <td>{document.estimatedCostData.bestEstPages}</td>
                        <td>{document.estimatedCostData.worstEstPages}</td>
                        <td>{formatCurrency(parseFloat(document.estimatedCostData.avgCost))}</td>
                        <td>{formatCurrency(parseFloat(document.estimatedCostData.bestCost))}</td>
                        <td>{formatCurrency(parseFloat(document.estimatedCostData.worstCost))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>

        </div>



        {sections.length > 0 && (
          <div>
            <h6>Sections Status</h6>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Section ID</th>
                    <th>Page Number</th>
                    <th>Status</th>
                    <th>Error (if any)</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section) => (
                    <tr key={section.sectionId}>
                      <td>{section.sectionId}</td>
                      <td>{section.pageNumber || 'N/A'}</td>
                      <td>{getStatusBadge(section.status)}</td>
                      <td>
                        {section.errorMessage ? (
                          <span className="text-danger">{section.errorMessage}</span>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};